import { onBeforeUnmount, ref } from "vue";
import { useIntersectionObserver } from "@vueuse/core";
import { cleanParams, useSiteComponentTracking } from "~/composables/useSiteComponentTracking";

/**
 * 首页模块埋点 composable，基于全站公共 tracking 扩展。
 */
export function useHomepageTracking(deviceType = "pc") {
	return useSiteComponentTracking(deviceType);
}

/**
 * 模块曝光：进入视窗 50% 以上且停留 1 秒后触发 section_view，同一组件实例内去重。
 */
export function useSectionView(
	targetRef,
	{ track, sectionId, getExtraParams, threshold = 0.5, dwellMs = 1000 }
) {
	const tracked = ref(false);
	let dwellTimer = null;

	const clearDwellTimer = () => {
		if (dwellTimer) {
			clearTimeout(dwellTimer);
			dwellTimer = null;
		}
	};

	const { stop } = useIntersectionObserver(
		targetRef,
		([entry]) => {
			if (tracked.value) return;

			if (entry?.isIntersecting && entry.intersectionRatio >= threshold) {
				clearDwellTimer();
				dwellTimer = setTimeout(() => {
					if (tracked.value) return;
					tracked.value = true;
					const extra = typeof getExtraParams === "function" ? getExtraParams() : {};
					track("section_view", cleanParams({ section_id: sectionId, ...extra }));
					stop();
				}, dwellMs);
				return;
			}

			clearDwellTimer();
		},
		{ threshold: [0, threshold, 1] }
	);

	onBeforeUnmount(() => {
		clearDwellTimer();
		stop();
	});

	return { tracked, stop };
}

/**
 * 导师卡曝光：进入视窗 50% 以上且停留 1 秒后触发，同一 mentor_id 在当前页只上报一次。
 */
export function useMentorCardImpression({ track, sectionId = "global_mentors" }) {
	const impressedIds = new Set();
	const observerStops = new Map();

	onBeforeUnmount(() => {
		observerStops.forEach(stop => stop());
		observerStops.clear();
	});

	function bindCardRef(el, teacher, index) {
		const mentorId = teacher?.uid || teacher?.id;
		if (!mentorId) return;

		if (!el) {
			observerStops.get(mentorId)?.();
			observerStops.delete(mentorId);
			return;
		}

		if (impressedIds.has(mentorId) || observerStops.has(mentorId)) return;

		const targetRef = ref(el);
		let dwellTimer = null;

		const clearDwellTimer = () => {
			if (dwellTimer) {
				clearTimeout(dwellTimer);
				dwellTimer = null;
			}
		};

		const { stop } = useIntersectionObserver(
			targetRef,
			([entry]) => {
				if (impressedIds.has(mentorId)) return;

				if (entry?.isIntersecting && entry.intersectionRatio >= 0.5) {
					clearDwellTimer();
					dwellTimer = setTimeout(() => {
						if (impressedIds.has(mentorId)) return;
						impressedIds.add(mentorId);
						track(
							"mentor_card_impression",
							cleanParams({
								section_id: sectionId,
								mentor_id: String(mentorId),
								mentor_name: teacher.name,
								position: index + 1,
								rating: Number.parseFloat(teacher.rating) || undefined,
							})
						);
						stop();
						observerStops.delete(mentorId);
					}, 1000);
					return;
				}

				clearDwellTimer();
			},
			{ threshold: [0, 0.5, 1] }
		);

		observerStops.set(mentorId, () => {
			clearDwellTimer();
			stop();
		});
	}

	return { bindCardRef };
}

export function formatMentorTags(tags) {
	if (!tags?.length) return undefined;
	return tags.join(",");
}

export function getScrollDepthPercent() {
	if (!import.meta.client) return undefined;
	const scrollTop = window.scrollY || document.documentElement.scrollTop;
	const docHeight = document.documentElement.scrollHeight - window.innerHeight;
	if (docHeight <= 0) return 0;
	return Math.round((scrollTop / docHeight) * 100);
}

/**
 * 视频卡曝光：进入视窗 50% 以上且停留 1 秒后触发，同一 content_id 在当前页只上报一次。
 */
export function useVideoCardImpression({ track }) {
	const impressedIds = new Set();
	const observerStops = new Map();

	onBeforeUnmount(() => {
		observerStops.forEach(stop => stop());
		observerStops.clear();
	});

	function bindVideoRef(el, video, index, mentorId) {
		const contentId = video?.id;
		if (!contentId) return;

		const impressionKey = String(contentId);

		if (!el) {
			observerStops.get(impressionKey)?.();
			observerStops.delete(impressionKey);
			return;
		}

		if (impressedIds.has(impressionKey) || observerStops.has(impressionKey)) return;

		const targetRef = ref(el);
		let dwellTimer = null;

		const clearDwellTimer = () => {
			if (dwellTimer) {
				clearTimeout(dwellTimer);
				dwellTimer = null;
			}
		};

		const { stop } = useIntersectionObserver(
			targetRef,
			([entry]) => {
				if (impressedIds.has(impressionKey)) return;

				if (entry?.isIntersecting && entry.intersectionRatio >= 0.5) {
					clearDwellTimer();
					dwellTimer = setTimeout(() => {
						if (impressedIds.has(impressionKey)) return;
						impressedIds.add(impressionKey);
						track(
							"video_card_impression",
							cleanParams({
								content_id: impressionKey,
								content_title: video.title,
								mentor_id: mentorId ? String(mentorId) : undefined,
								position: index + 1,
							})
						);
						stop();
						observerStops.delete(impressionKey);
					}, 1000);
					return;
				}

				clearDwellTimer();
			},
			{ threshold: [0, 0.5, 1] }
		);

		observerStops.set(impressionKey, () => {
			clearDwellTimer();
			stop();
		});
	}

	return { bindVideoRef };
}

/**
 * 测算工具卡曝光：进入视窗 50% 以上且停留 1 秒后触发，同一 tool_type 在当前页只上报一次。
 */
export function useToolCardImpression({ track }) {
	const impressedIds = new Set();
	const observerStops = new Map();

	onBeforeUnmount(() => {
		observerStops.forEach(stop => stop());
		observerStops.clear();
	});

	function bindToolRef(el, item, index) {
		const toolType = item?.id;
		if (!toolType) return;

		if (!el) {
			observerStops.get(toolType)?.();
			observerStops.delete(toolType);
			return;
		}

		if (impressedIds.has(toolType) || observerStops.has(toolType)) return;

		const targetRef = ref(el);
		let dwellTimer = null;

		const clearDwellTimer = () => {
			if (dwellTimer) {
				clearTimeout(dwellTimer);
				dwellTimer = null;
			}
		};

		const { stop } = useIntersectionObserver(
			targetRef,
			([entry]) => {
				if (impressedIds.has(toolType)) return;

				if (entry?.isIntersecting && entry.intersectionRatio >= 0.5) {
					clearDwellTimer();
					dwellTimer = setTimeout(() => {
						if (impressedIds.has(toolType)) return;
						impressedIds.add(toolType);
						track(
							"tool_card_impression",
							cleanParams({
								tool_type: toolType,
								tool_name: item.title,
								position: index + 1,
							})
						);
						stop();
						observerStops.delete(toolType);
					}, 1000);
					return;
				}

				clearDwellTimer();
			},
			{ threshold: [0, 0.5, 1] }
		);

		observerStops.set(toolType, () => {
			clearDwellTimer();
			stop();
		});
	}

	return { bindToolRef };
}
